import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import type { BlockRenderProps } from '../../types';

export function DynamicCocktailSpotlight({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const cocktailName = pickBlockField(block.content, 'cocktailName', locale);
  const description = pickBlockField(block.content, 'description', locale);
  const price = pickBlockField(block.content, 'price', locale);
  const badgeText = pickBlockField(block.content, 'badgeText', locale);
  const image = pickMediaUrl(block, 'cocktailImage');
  const imageSrc = image ? buildSiteImageProxyUrl(image, 960, 78) : undefined;
  const imageSrcSet = image ? buildSiteImageSrcSet(image, [480, 720, 960], 78) : undefined;
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-cocktail', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      id="cocktail"
    >
      <div className="ws-cocktail__inner">
        <div className="ws-cocktail__visual">
          {imageSrc ? (
            <EditableMedia
              fieldKey="cocktailImage"
              editable={editable}
              activeEditTarget={target}
              className="ws-cocktail__photo"
              src={imageSrc}
              srcSet={imageSrcSet}
              sizes={SITE_IMAGE_SIZES.heroPedestal}
              alt={cocktailName || sectionTitle || 'Cocktail'}
              width={720}
              height={720}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="ws-cocktail__placeholder" aria-hidden />
          )}
        </div>
        <div className="ws-cocktail__copy">
          {badgeText || editable ? (
            <EditableContent
              fieldKey="badgeText"
              editable={editable}
              activeEditTarget={target}
              className="ws-cocktail__badge"
            >
              {badgeText}
            </EditableContent>
          ) : null}
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-cocktail__eyebrow"
          >
            {sectionTitle}
          </EditableContent>
          <EditableContent
            fieldKey="cocktailName"
            editable={editable}
            activeEditTarget={target}
            className="ws-cocktail__name"
          >
            {cocktailName}
          </EditableContent>
          <EditableContent
            fieldKey="description"
            editable={editable}
            activeEditTarget={target}
            className="ws-cocktail__description"
          >
            {description}
          </EditableContent>
          {price || editable ? (
            <EditableContent
              fieldKey="price"
              editable={editable}
              activeEditTarget={target}
              className="ws-cocktail__price"
            >
              {price}
            </EditableContent>
          ) : null}
        </div>
      </div>
    </section>
  );
}
